import { Link } from "react-router-dom";
import { Clock, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { MS_PER_DAY } from "@/lib/constants";

function isTrialHiddenForUser(user) {
    if (!user) return true;
    if (user.role === "admin") return true;
    return !["trial", "free"].includes(user.plan);
}

function computeDaysLeft(user) {
    if (!user.trial_until) return { trialEnd: null, msLeft: 0, daysLeft: 0 };
    const trialEnd = new Date(user.trial_until);
    const msLeft = trialEnd - new Date();
    const daysLeft = Math.max(0, Math.ceil(msLeft / MS_PER_DAY));
    return { trialEnd, msLeft, daysLeft };
}

/**
 * Returns one of three trial states:
 *  - "hidden"  → paid plan / admin → no banner
 *  - "expired" → trial over OR free with no trial history
 *  - "active"  → trial still running (carries days left & urgency flag)
 */
function getTrialState(user) {
    if (isTrialHiddenForUser(user)) return { kind: "hidden" };
    const { trialEnd, msLeft, daysLeft } = computeDaysLeft(user);
    const expired = !trialEnd || msLeft <= 0 || (user.plan === "free" && !user.trial_until);
    if (expired) return { kind: "expired" };
    return { kind: "active", daysLeft, urgent: daysLeft <= 2 };
}

export default function TrialBanner() {
    const { user } = useAuth();
    const state = getTrialState(user);
    if (state.kind === "hidden") return null;
    if (state.kind === "expired") return <ExpiredBanner />;
    return <ActiveTrialBanner daysLeft={state.daysLeft} urgent={state.urgent} />;
}

function ExpiredBanner() {
    return (
        <div className="glass rounded-2xl p-5 mb-6 border-red-500/30 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="trial-banner-expired">
            <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-2xl bg-red-500/15 flex items-center justify-center text-2xl">⚠️</div>
                <div>
                    <div className="font-display font-bold text-base text-red-300">Your trial has ended</div>
                    <div className="text-sm text-gray-400">Upgrade to keep collecting WhatsApp leads. Existing leads stay safe.</div>
                </div>
            </div>
            <UpgradeCta label="Upgrade now" />
        </div>
    );
}

function ActiveTrialBanner({ daysLeft, urgent }) {
    const containerClass = urgent ? "border-amber-500/40" : "border-brand-500/30 glow-green";
    const iconClass = urgent ? "bg-amber-500/15 text-amber-300" : "bg-brand-500/15 text-brand-300";
    return (
        <div className={`glass rounded-2xl p-5 mb-6 flex flex-col md:flex-row items-center justify-between gap-4 ${containerClass}`} data-testid="trial-banner-active">
            <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${iconClass}`}>
                    {urgent ? <Clock className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                </div>
                <ActiveTrialMessage daysLeft={daysLeft} urgent={urgent} />
            </div>
            <UpgradeCta label="Upgrade" />
        </div>
    );
}

function ActiveTrialMessage({ daysLeft, urgent }) {
    const dayWord = `${daysLeft} day${daysLeft !== 1 ? "s" : ""}`;
    return (
        <div>
            <div className="font-display font-bold text-base">
                {urgent
                    ? <>Trial ends in <span className="text-amber-300">{dayWord}</span> — don't lose your leads</>
                    : <>You're on the <span className="gradient-text">7-day trial</span> · {dayWord} left</>}
            </div>
            <div className="text-sm text-gray-400">
                Use code <span className="font-mono text-brand-300">CONNEXA03</span> for 30 days FREE on Growth · CSV export · unlimited leads
            </div>
        </div>
    );
}

function UpgradeCta({ label }) {
    return (
        <Link to="/pricing" className="btn-primary px-6 py-3 rounded-xl font-semibold text-white flex items-center gap-2 whitespace-nowrap" data-testid="trial-upgrade-cta">
            {label} <ArrowRight className="w-4 h-4" />
        </Link>
    );
}
